import { useQuery } from "@tanstack/react-query";
import { fromJS } from "immutable";
import { useSelector } from "react-redux";
import { aggregateDataByFieldId } from "../../api/DataApi";
import { buildComparisonFilters, selectReportData } from "../selectors/reportSelectors";

export const useAggregatedData = (
  projectId: string,
  viewBy: string,
  segmentBy: string,
  dateUnit: string,
  filters: any,
  objectType: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["aggregatedData", projectId, viewBy, segmentBy, dateUnit, filters, objectType],
    queryFn: async () => {
      return await aggregateDataByFieldId(projectId, viewBy, segmentBy, dateUnit, filters, [
        objectType,
      ]);
    },
    enabled: enabled && !!viewBy && !!projectId && !!objectType,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useAggregatedDataCompare = (
  projectId: string,
  viewBy: string,
  segmentBy: string,
  dateUnit: string,
  filters: any,
  objectType: string,
  comparisonBy?: string,
  enabled: boolean = true,
  useReduxStore: boolean = true // New parameter to control data source
) => {
  const reportData = useSelector(selectReportData);

  // Choose data source based on useReduxStore flag
  const dataSource = useReduxStore
    ? reportData
    : fromJS({
        comparisonBy: comparisonBy || "none",
        filters,
        dateUnit,
      });

  const { currentFilters, previousFilters, hasComparison } = buildComparisonFilters(
    dataSource as any
  );

  return useQuery({
    queryKey: [
      "aggregatedDataCompare",
      projectId,
      viewBy,
      segmentBy,
      dateUnit,
      currentFilters,
      previousFilters,
      objectType,
      useReduxStore ? "redux" : "widget", // Different cache keys
    ],
    queryFn: async () => {
      // Call two APIs separately for current and previous filters when comparison is enabled
      if (hasComparison) {
        const [currentData, previousData] = await Promise.all([
          aggregateDataByFieldId(
            projectId,
            viewBy,
            segmentBy,
            dateUnit,
            currentFilters || fromJS({}),
            [objectType]
          ),
          aggregateDataByFieldId(
            projectId,
            viewBy,
            segmentBy,
            dateUnit,
            previousFilters || fromJS({}),
            [objectType]
          ),
        ]);

        return { currentData, previousData };
      }

      // Fallback to single call when no comparison
      const currentData = await aggregateDataByFieldId(
        projectId,
        viewBy,
        segmentBy,
        dateUnit,
        currentFilters || fromJS({}),
        [objectType]
      );
      return { currentData, previousData: [] };
    },
    enabled: enabled && !!viewBy && !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useNumberData = (
  projectId: string,
  viewBy: string,
  segmentBy: string,
  dateUnit: string,
  valueKey: string,
  filters: any,
  objectType: string,
  enabled: boolean = true,
  comparisonBy?: string,
  useReduxStore: boolean = true // New parameter to control data source
) => {
  const reportData = useSelector(selectReportData);

  const dataSource = useReduxStore
    ? reportData
    : fromJS({
        comparisonBy: comparisonBy || "none",
        filters,
        dateUnit,
      });

  const { currentFilters, previousFilters, hasComparison } = buildComparisonFilters(
    dataSource as any
  );

  return useQuery({
    queryKey: [
      "numberData",
      projectId,
      viewBy,
      segmentBy,
      dateUnit,
      valueKey,
      currentFilters,
      previousFilters,
      objectType,
      useReduxStore ? "redux" : "widget", // Different cache keys
    ],
    queryFn: async () => {
      // Call APIs with current/previous filters directly (align with line compare)
      const [currentItems, prevItems] = await Promise.all([
        aggregateDataByFieldId(
          projectId,
          viewBy,
          segmentBy,
          dateUnit,
          currentFilters || fromJS({}),
          [objectType]
        ),
        hasComparison
          ? aggregateDataByFieldId(
              projectId,
              viewBy,
              segmentBy,
              dateUnit,
              previousFilters || fromJS({}),
              [objectType]
            )
          : Promise.resolve([]),
      ]);

      const currentValue = (currentItems || []).reduce(
        (total: number, item: any) => total + (Number(item[valueKey]) || 0),
        0
      );
      const previousValue = (prevItems || []).reduce(
        (total: number, item: any) => total + (Number(item[valueKey]) || 0),
        0
      );

      let delta = "";
      let deltaTone: "up" | "down" = "up";

      // Always show delta when comparison is enabled, even if no previous data
      if (hasComparison) {
        if (previousValue === 0) {
          delta = currentValue > 0 ? "↑ 100%" : "0%";
        } else {
          const percentageChange = ((currentValue - previousValue) / previousValue) * 100;
          const roundedPercentage = Math.round(percentageChange);
          if (roundedPercentage > 0) {
            delta = `↑ ${roundedPercentage}%`;
            deltaTone = "up";
          } else if (roundedPercentage < 0) {
            delta = `↓ ${Math.abs(roundedPercentage)}%`;
            deltaTone = "down";
          } else {
            delta = "0%";
            deltaTone = "up";
          }
        }
      }

      return { value: currentValue, previousValue, delta, deltaTone };
    },
    enabled: enabled && !!viewBy && !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};
